// frontend/src/pages/PayrollItems/styles.ts
// Palette + reusable style objects (buttons, chips, badges) for PayrollItems.

import type { CSSProperties } from "react";

// ── Palette ─────────────────────────────────────────────────────────────
export const BRAND = "#0F9599";
export const BRAND_DARK = "#0F6E56";
export const TEXT_PRIMARY = "#111827";
export const TEXT_INK = "#1A2B2B";
export const TEXT_SECONDARY = "#6B7280";
export const TEXT_TERTIARY = "#9CA3AF";
export const BG_CARD = "#FFFFFF";
export const BG_PAGE = "#F7F9F9";
export const BORDER = "#E5E7EB";
export const BORDER_LIGHT = "#F0F4F4";
export const WARN_TEXT = "#92400E";
export const WARN_SOFT = "#FEF3C7";
export const SUCCESS_TEXT = "#166534";
export const SUCCESS_SOFT = "#DCFCE7";
export const INFO_TEXT = "#185FA5";

// ── Buttons ─────────────────────────────────────────────────────────────
/** Primary solid-teal button ("Done" footer). */
export const BTN_TEAL: CSSProperties = {
  background: BRAND,
  color: "white",
  fontSize: 14,
  fontWeight: 600,
  padding: "10px 18px",
  border: "none",
  borderRadius: 9,
  cursor: "pointer",
  fontFamily: "inherit",
};

/** Outline-teal button with icon support ("New payroll item"). */
export const BTN_TEAL_OUT: CSSProperties = {
  background: BG_CARD,
  color: BRAND_DARK,
  fontSize: 13.5,
  fontWeight: 600,
  padding: "9px 14px",
  border: "0.5px solid " + BRAND,
  borderRadius: 9,
  cursor: "pointer",
  fontFamily: "inherit",
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
};

/** Neutral outline button ("Try spreadsheet import"). */
export const BTN_OUT: CSSProperties = {
  background: BG_CARD,
  color: TEXT_PRIMARY,
  fontSize: 13.5,
  fontWeight: 600,
  padding: "9px 14px",
  border: "0.5px solid " + BORDER,
  borderRadius: 9,
  cursor: "pointer",
  fontFamily: "inherit",
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
};

/** Transparent "ghost" button ("Cancel" footer). */
export const BTN_GHOST: CSSProperties = {
  background: "transparent",
  color: TEXT_SECONDARY,
  fontSize: 13.5,
  fontWeight: 600,
  padding: "9px 14px",
  border: "none",
  borderRadius: 9,
  cursor: "pointer",
  fontFamily: "inherit",
};

/** Small square icon-only button (back arrow, close X). */
export const ICON_BTN: CSSProperties = {
  width: 34,
  height: 34,
  display: "grid",
  placeItems: "center",
  borderRadius: 8,
  color: TEXT_SECONDARY,
  cursor: "pointer",
};

/** Inline clickable text link (brand-dark colored). */
export const LINK: CSSProperties = {
  color: BRAND_DARK,
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};

// ── Status chips ────────────────────────────────────────────────────────
/** Green "All set" / "Active" chip. */
export const CHIP_OK: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  fontSize: 12,
  fontWeight: 600,
  padding: "3px 9px",
  borderRadius: 12,
  background: SUCCESS_SOFT,
  color: SUCCESS_TEXT,
};

/** Amber "None yet" chip. */
export const CHIP_WARN: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  fontSize: 12,
  fontWeight: 600,
  padding: "3px 9px",
  borderRadius: 12,
  background: WARN_SOFT,
  color: WARN_TEXT,
};

// ── Badges ──────────────────────────────────────────────────────────────
/** Blue "DEFAULT" badge for schedules. */
export const BADGE: CSSProperties = {
  display: "inline-block",
  background: INFO_TEXT,
  color: "white",
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.05em",
  padding: "3px 9px",
  borderRadius: 6,
};

/** Teal "PRIMARY" badge for locations. */
export const BADGE_PRIMARY: CSSProperties = {
  ...BADGE,
  background: BRAND,
};